import { Injectable } from "@angular/core";

type WsRequest = {
  id: string;
  method: string;
  path: string;
  query?: Record<string, any>;
  body?: any;
};

type WsResponse = {
  id?: string;
  ok?: boolean;
  statusCode?: number;
  data?: any;
  error?: any;
};

@Injectable({
  providedIn: "root",
})
export class RelayerWsService {
  private baseUrl: string | null = null;
  private ws: WebSocket | null = null;
  private wsUrl: string | null = null;
  private connectPromise: Promise<void> | null = null;
  private nextId = 1;
  private pending = new Map<
    string,
    { resolve: (value: any) => void; reject: (reason?: any) => void; timeout: any }
  >();

  private sanitizePath(path: string) {
    if (!path) return null;
    if (!path.startsWith("/")) return null;
    if (path.includes("..")) return null;
    return path;
  }

  setBaseUrl(url: string | null) {
    const normalized = url?.trim() || null;
    if (normalized === this.baseUrl) return;

    this.baseUrl = normalized;
    this.wsUrl = normalized ? this.toWsUrl(normalized) : null;
    this.teardownSocket();
  }

  async request<T = any>(
    path: string,
    opts?: { method?: string; query?: Record<string, any>; body?: any; timeoutMs?: number }
  ): Promise<T> {
    if (!this.baseUrl || !this.wsUrl) {
      throw new Error("Relayer base URL is not configured");
    }

    const requestPath = this.sanitizePath(path);
    if (!requestPath) {
      throw new Error("Invalid relayer request path");
    }
    const payload: WsRequest = {
      id: String(this.nextId++),
      method: (opts?.method || (opts?.body ? "POST" : "GET")).toUpperCase(),
      path: requestPath,
      query: opts?.query,
      body: opts?.body,
    };

    try {
      await this.ensureConnected();
      return await this.sendWsRequest<T>(payload, opts?.timeoutMs ?? 15000);
    } catch (wsError) {
      console.warn("[relayer-ws] WS request failed; falling back to HTTP", wsError);
      return this.requestHttp<T>(payload);
    }
  }

  private sendWsRequest<T>(payload: WsRequest, timeoutMs: number): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const id = payload.id as string;
      const timeout = setTimeout(() => {
        this.pending.delete(id);
        reject(new Error(`Relayer WS timeout for ${payload.path}`));
      }, timeoutMs);

      this.pending.set(id, { resolve, reject, timeout });
      this.ws?.send(JSON.stringify(payload));
    });
  }

  private async requestHttp<T>(payload: WsRequest): Promise<T> {
    if (!this.baseUrl) throw new Error("Relayer base URL is not configured");

    const baseUrl = this.httpBaseForPath(payload.path || "/");
    const url = new URL(`${baseUrl}${payload.path}`);
    for (const [key, value] of Object.entries(payload.query || {})) {
      if (value !== undefined && value !== null) url.searchParams.set(key, String(value));
    }

    const response = await fetch(url.toString(), {
      method: payload.method || "GET",
      headers: { "content-type": "application/json" },
      body: payload.method === "GET" || payload.method === "HEAD"
        ? undefined
        : JSON.stringify(payload.body ?? {}),
    });
    const text = await response.text();
    let parsed: any = text;
    try {
      parsed = text ? JSON.parse(text) : null;
    } catch {}

    if (!response.ok) {
      throw new Error(parsed?.error || parsed?.message || text || `Relayer HTTP ${response.status}`);
    }

    return parsed as T;
  }

  private async ensureConnected(): Promise<void> {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) return;
    if (this.connectPromise) return this.connectPromise;
    if (!this.wsUrl) throw new Error("Relayer WS URL is not configured");

    this.connectPromise = new Promise<void>((resolve, reject) => {
      const ws = new WebSocket(this.wsUrl as string);
      this.ws = ws;

      ws.onopen = () => {
        this.connectPromise = null;
        resolve();
      };

      ws.onmessage = (event) => this.handleMessage(event.data);

      ws.onerror = () => {
        if (this.connectPromise) {
          this.connectPromise = null;
          reject(new Error("Failed to connect to relayer WS"));
        }
      };

      ws.onclose = () => {
        this.rejectAllPending("Relayer WS disconnected");
        this.ws = null;
      };
    });

    return this.connectPromise;
  }

  private handleMessage(raw: any) {
    let msg: WsResponse;
    try {
      msg = JSON.parse(raw);
    } catch {
      return;
    }

    if (!msg.id) return;
    const pending = this.pending.get(msg.id);
    if (!pending) return;

    clearTimeout(pending.timeout);
    this.pending.delete(msg.id);

    if (msg.ok) {
      pending.resolve(msg.data);
    } else {
      pending.reject(new Error(msg.error || "Relayer WS request failed"));
    }
  }

  private teardownSocket() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.connectPromise = null;
    this.rejectAllPending("Relayer WS reconfigured");
  }

  private rejectAllPending(reason: string) {
    for (const [, pending] of this.pending.entries()) {
      clearTimeout(pending.timeout);
      pending.reject(new Error(reason));
    }
    this.pending.clear();
  }

  private toWsUrl(url: string): string {
    const trimmed = url.replace(/\/+$/, "");
    const relayerWs = this.relayerWsUrl(trimmed);
    if (relayerWs) return relayerWs;
    if (trimmed.startsWith("wss://") || trimmed.startsWith("ws://")) {
      return `${trimmed}/ws`;
    }
    if (trimmed.startsWith("https://")) {
      return `${this.trimRelayerPath(trimmed.replace(/^https:\/\//, "wss://"))}/ws`;
    }
    if (trimmed.startsWith("http://")) {
      return `${this.trimRelayerPath(trimmed.replace(/^http:\/\//, "ws://"))}/ws`;
    }
    return `${this.trimRelayerPath(`wss://${trimmed}`)}/ws`;
  }

  private trimRelayerPath(url: string): string {
    return url.replace(/\/relayer\/?$/, "");
  }

  private relayerWsUrl(url: string): string | null {
    try {
      const parsed = new URL(url);
      if (!parsed.pathname.replace(/\/+$/, "").endsWith("/relayer")) return null;
      if (parsed.hostname === "testnet-api.layerwallet.com") {
        return "ws://testnet-api.layerwallet.com:8000/ws";
      }
      parsed.protocol = parsed.protocol === "https:" ? "wss:" : "ws:";
      parsed.pathname = "/ws";
      return parsed.toString();
    } catch {
      return null;
    }
  }

  private httpBaseForPath(path: string): string {
    const trimmed = (this.baseUrl || "").replace(/\/+$/, "");
    if (/^\/(address|tx|chain)\//.test(path)) {
      return trimmed.replace(/\/relayer$/, "");
    }
    return trimmed;
  }
}
